import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { ScrollView, View, Text, TouchableOpacity, Platform } from "react-native"
import { usePlaidLink } from "../utility/context"
import { textStyles, ShadowView } from "../style"
import { ThemeContext } from "../utility/context/ThemeContext"
import { useAppDispatch, useAppSelector } from '../store'
import { Ionicons } from "@expo/vector-icons"
import ThemedDialog from "../style/dialog"
import { MAX_CONTENT_WIDTH } from "../config"
import { currencyFormat } from "@ecountabl/lib"
import Badge from "../utility/badge"
import { useNavigation } from "@react-navigation/native"
import Sortable from "react-native-sortables"
import api from "../api-client"
import Haptics from "../utility/haptics"
import { reorderByItemIds } from "../accounts-slice"
import { ManageAccountsSkeleton } from "./manage-accounts-skeleton"

type InstitutionItem = {
  item_id: string
  name: string
  totalBalance: number
  error?: boolean
}

type SortOrderParams = {
  fromIndex: number
  toIndex: number
  indexToKey: string[]
  keyToIndex: Record<string, number>
  key: string
}

const ManageAccounts= (): JSX.Element => {
  const theme = useContext(ThemeContext).theme
  const { textMain, bgMain, danger, white, bgAlt, borderColor, primary } = theme.colors

  const { startPlaidLinkSession } = usePlaidLink()
  const { status, institutions } = useAppSelector((state) => state.accountsSlice)
  const navigation = useNavigation()
  const dispatch = useAppDispatch()
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingOrderRef = useRef<string[] | null>(null)

  const MANAGE_ACCOUNTS_PATH = "settings/manage-accounts"

  const institutionsNeedingRelink = useMemo(() => {
    return institutions.filter((institution) => institution.error)
  }, [institutions])

  const [relinkError, setRelinkError] = React.useState(false)
  const [isSorting, setIsSorting] = useState(false)

  const numAccountsNeedRelinkMessage = useMemo(() => {
    if (status === "loading" || institutionsNeedingRelink.length === 0) return ""

    if (institutionsNeedingRelink.length === 1) return "account needs to be reconnected"
    return "accounts need to be reconnected"
  }, [institutionsNeedingRelink.length, status])

  // unmount cleanup of debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  if (status === "loading") {
    return <ManageAccountsSkeleton rowCount={6} />
  }

  // Empty state when there are no linked institutions
  if (!institutions || institutions.length === 0) {
    return (
      <View style={{ flex: 1, backgroundColor: bgMain, alignItems: "center", justifyContent: "center", paddingHorizontal: 24 }}>
        <Ionicons name="wallet-outline" size={72} color={textMain} style={{ marginBottom: 12, opacity: 0.8 }} />
        <Text style={[textStyles.h4, { color: textMain, textAlign: "center", marginBottom: 6 }]}>
          No linked institutions yet
        </Text>
        <Text style={[textStyles.body, { color: textMain, textAlign: "center" }]}>
          You don&apos;t have any accounts linked. Tap{" "}
          <Text
            onPress={() => {
              startPlaidLinkSession({ redirectPath: MANAGE_ACCOUNTS_PATH })
            }}
            style={{color: primary, textDecorationLine: "underline" }}
          >
            here
          </Text>{" "}
          to link an account.
        </Text>
      </View>
    )
  }

  return <>
    <View style={{flex: 1, backgroundColor: bgMain }}>
      <View style={{paddingHorizontal: 16, gap: 8, paddingVertical: 16, width: "100%", maxWidth: MAX_CONTENT_WIDTH, alignSelf: "center"}}>
        <Text style={[textStyles.h4, {color: textMain}]}>
          Your linked institution{institutions.length > 1 ? "s" : ""}
        </Text>
        {
          institutionsNeedingRelink.length ?
          <View style={{gap: 8}}>
            <View style={{flexDirection: "row", alignItems: "center", gap: 5}}>
              <Badge
                icon={<Text style={{color: white}}>{institutionsNeedingRelink.length}</Text>}
                style={{backgroundColor: danger}}
              />
              <Text style={[textStyles.body, {color: textMain}]}>
                {numAccountsNeedRelinkMessage}
              </Text>
            </View>
          </View>
          : null
        }
      </View>
      <ScrollView
        contentContainerStyle={{
          paddingTop: 10,
          paddingHorizontal: 16,
          gap: 10,
          width: "100%",
          maxWidth: MAX_CONTENT_WIDTH,
          alignSelf: "center"
        }}
        scrollEnabled={!isSorting}
      >
        <View style={{ width: "100%", maxWidth: MAX_CONTENT_WIDTH, alignSelf: "center" }}>
          <Sortable.Grid
            data={institutions}
            columns={1}
            hapticsEnabled
            customHandle
            onDragStart={() => {
              setIsSorting(true)
              Haptics.selection()
            }}
            onDragEnd={() => {
              Haptics.impactLight()
              setIsSorting(false)
              // Commit latest order once dragging ends
              const orderedKeys = Array.isArray(pendingOrderRef.current)
                ? pendingOrderRef.current
                : null
              if (orderedKeys && orderedKeys.length) {
                // Optimistically reorder global state so other screens reflect it after drop
                dispatch(reorderByItemIds(orderedKeys) as any)
                if (debounceTimer.current) clearTimeout(debounceTimer.current)
                debounceTimer.current = setTimeout(() => {
                  // Persist new order
                  api.updateAccountsOrder(orderedKeys).catch(() => void 0)
                }, 800)
              }
              // Reset pending order
              pendingOrderRef.current = null
            }}
            keyExtractor={(item: InstitutionItem) => item.item_id}
            renderItem={({ item }: { item: InstitutionItem }) => {
              const needsRelink = item.error
              return (
                <View style={{ width: "100%" }}>
                  <ShadowView
                    style={{
                      width: "100%",
                      paddingHorizontal: 12,
                      paddingVertical: 12,
                      borderRadius: 8,
                      backgroundColor: bgAlt,
                      borderWidth: 1,
                      borderColor: needsRelink ? danger : borderColor,
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      marginBottom: 10
                    }}
                  >
                    {
                      institutions.length > 1 ?
                      <Sortable.Handle>
                        <View
                          style={
                            Platform.OS === 'web'
                            ? ({ cursor: isSorting ? 'grabbing' : 'grab' } as any)
                            : {}
                          }
                        >
                          <Ionicons name="reorder-three-outline" size={18} color={needsRelink ? danger : textMain} />
                        </View>
                      </Sortable.Handle>
                      : null
                    }
                    
                    <TouchableOpacity
                      style={{ flex: 1 }}
                      onPress={() => {
                        Haptics.selection()
                        if (needsRelink) {
                          startPlaidLinkSession({
                            item_id: item.item_id,
                            redirectPath: MANAGE_ACCOUNTS_PATH,
                          })
                        } else {
                          (navigation as any).navigate('InstitutionDetails', { item_id: item.item_id })
                        }
                      }}
                      activeOpacity={0.85}
                    >
                      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                        <Text
                          style={[
                            textStyles.h4,
                            { color: needsRelink ? danger : textMain, flex: 1 }
                          ]}
                          numberOfLines={1}
                          ellipsizeMode="tail"
                        >
                          {item.name}
                        </Text>
                        <Text style={[textStyles.body, { color: needsRelink ? danger : textMain }]}>
                          {needsRelink ? "Reconnect" : currencyFormat(item.totalBalance)}
                        </Text>
                        {
                          !needsRelink ? (
                            <Ionicons name="chevron-forward-outline" size={18} color={needsRelink ? danger : textMain} />
                          ) : null
                        }
                      </View>
                    </TouchableOpacity>
                  </ShadowView>
                </View>
              )
              
            }}
            onOrderChange={(params: SortOrderParams) => {
              const orderedKeys = Array.isArray(params?.indexToKey) ? params.indexToKey : []
              // Track latest order as the user drags; don't mutate external data mid-gesture
              if (orderedKeys.length) {
                pendingOrderRef.current = orderedKeys
              }
            }}
          />
        </View>
      </ScrollView>
    </View>
    {/* relinking error modal */}
    <ThemedDialog
      options={[
        {
          title: "Okay",
          icon: <Ionicons name="checkmark" size={22}/>,
          role: "default",
          onPress: () => setRelinkError(false)
        }
      ]}
      visible={relinkError}
      errorAnimation={relinkError}
      backDropProps={{onPress: () => setRelinkError(false)}}
      title={"An error occurred while relinking this account"}
      subMessage="Please try again later"
      loading={false}
    />
  </>
}

export default ManageAccounts